import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ClaimUnderstandingDetail from '../components/aiIntelligence/ClaimUnderstandingDetail';
import PolicyRulesDetail from '../components/aiIntelligence/PolicyRulesDetail';
import FWARiskDetail from '../components/aiIntelligence/FWARiskDetail';
import ClinicalIntelligenceDetail from '../components/aiIntelligence/ClinicalIntelligenceDetail';
import UtilizationBillingDetail from '../components/aiIntelligence/UtilizationBillingDetail';
import ClaimSynthesisDetail from '../components/aiIntelligence/ClaimSynthesisDetail';
import RiskConfidenceDetail from '../components/aiIntelligence/RiskConfidenceDetail';
import StandardAIDetail from '../components/aiIntelligence/StandardAIDetail';
import { ArrowLeft, Download } from 'lucide-react';
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f3f5f7;
`;

const Header = styled.div`
  // background: white;
  padding: 1.5rem 2rem 0rem;
  // border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  gap: 1rem;
  // position: sticky;
  // top: 0;
  z-index: 100;
  // box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  width: 90%;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  background-color: #fff;
  border-radius: 35px;
  color: rgba(40,40,40,0.8);
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  // 00a1ff
  &:hover {
    background: #F3F4F6;
    border-color: #D1D5DB;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const DownloadReportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left:auto;
  padding: 0.5rem 1rem;
  background: transparent;
  background-color: #00a1ff0d;
  border: none;
  border-radius: 35px;
  color: #00a1ff;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  // 
  &:hover {
    background: #fff;
    border-color: #D1D5DB;
  }
`;

const ContentContainer = styled.div`
  max-width: 90%;
  margin: 0 auto;
  padding: 2rem;
`;

const AIIntelligenceDetail = () => {
  const { cardCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cardData = location.state?.card;

  useEffect(() => {
    // Force scroll to top targeting all possible scrollable containers
    const scrollToTop = () => {
      // Scroll window
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Find all scrollable elements
      const allElements = document.querySelectorAll('*');
      let scrollableElements = [];
      
      allElements.forEach(el => {
        const hasScroll = el.scrollHeight > el.clientHeight;
        const overflowY = window.getComputedStyle(el).overflowY;
        if (hasScroll && (overflowY === 'auto' || overflowY === 'scroll')) {
          scrollableElements.push(el);
          // console.log('Found scrollable element:', el.className, 'scrollTop:', el.scrollTop);
          el.scrollTop = 0;
        }
      });
      
      // console.log('Total scrollable elements found:', scrollableElements.length);
    };

    // Immediate scroll
    scrollToTop();
    
    // Check and scroll after delays to ensure component has loaded
    const timers = [
      setTimeout(() => {
        // console.log('Scroll attempt after 100ms');
        scrollToTop();
      }, 100),
      setTimeout(() => {
        // console.log('Scroll attempt after 300ms');
        scrollToTop();
      }, 300),
      setTimeout(() => {
        // console.log('Scroll attempt after 500ms');
        scrollToTop();
      }, 500)
    ];
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [cardCode, cardData]);

  const handleBack = () => {
    navigate(-1);
  };

  // Map card codes to their custom detail components
  const getDetailComponent = () => {
    // Check if card has a custom detailTemplate property
    const customTemplate = cardData?.detailTemplate;
    if (customTemplate) {
      // Use custom template if specified
      switch (customTemplate) {
        case 'ClaimUnderstandingDetail':
          return <ClaimUnderstandingDetail card={cardData} />;
        case 'PolicyRulesDetail':
          return <PolicyRulesDetail card={cardData} />;
        case 'FWARiskDetail':
          return <FWARiskDetail card={cardData} />;
        case 'ClinicalIntelligenceDetail':
          return <ClinicalIntelligenceDetail card={cardData} />;
        case 'UtilizationBillingDetail':
          return <UtilizationBillingDetail card={cardData} />;
        case 'ClaimSynthesisDetail':
          return <ClaimSynthesisDetail card={cardData} />;
        case 'RiskConfidenceDetail':
          return <RiskConfidenceDetail card={cardData} />;
        default:
          return <StandardAIDetail card={cardData} />;
      }
    } else {
      return <StandardAIDetail card={cardData} />;
    }

    // Fallback to code-based mapping
    switch (cardCode) {
      case 'AI_CARD_CLAIM_UNDERSTANDING':
        return <ClaimUnderstandingDetail card={cardData} />;
      case 'AI_CARD_POLICY_RULES_INSIGHT':
        return <PolicyRulesDetail card={cardData} />;
      case 'AI_CARD_FWA_RISK':
        return <FWARiskDetail card={cardData} />;
      case 'AI_CARD_CLINICAL_INTELLIGENCE':
        return <ClinicalIntelligenceDetail card={cardData} />;
      case 'AI_CARD_UTILIZATION_BILLING_INSIGHT':
        return <UtilizationBillingDetail card={cardData} />;
      case 'AI_CARD_CLAIM_SYNTHESIS':
        return <ClaimSynthesisDetail card={cardData} />;
      case 'AI_CARD_RISK_CONFIDENCE':
        return <RiskConfidenceDetail card={cardData} />;
      default:
        // Standard fallback template
        return <StandardAIDetail card={cardData} />;
    }
  };

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={25} />
          Back
        </BackButton>
        <HeaderTitle>{cardData?.title || 'AI Intelligence Detail'}</HeaderTitle>
        <DownloadReportButton>
          <Download size={30} />
          Download Report
        </DownloadReportButton>
      </Header>
      <ContentContainer>
        {getDetailComponent()}
      </ContentContainer>
    </PageContainer>
  );
};

export default AIIntelligenceDetail;
